import { useEffect, useRef } from 'react';

const HorizontalMatrixBackground = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const parentContainer = canvas.parentElement;
    let animationFrameId = null;
    let lastScrollHeight = 0;

    // Set canvas size to cover full scrollable area
    const resizeCanvas = () => {
      if (parentContainer) {
        const currentScrollHeight = parentContainer.scrollHeight;
        const currentWidth = parentContainer.offsetWidth;
        
        // Only resize if dimensions actually changed
        if (canvas.width !== currentWidth || canvas.height !== currentScrollHeight) {
          canvas.width = currentWidth;
          canvas.height = currentScrollHeight;
          lastScrollHeight = currentScrollHeight;
          
          console.log('Canvas resized to:', currentWidth, 'x', currentScrollHeight);
        }
      }
    };
    
    // Initial resize
    resizeCanvas();
    
    // Continuously check for size changes (messages being added)
    const checkResize = () => {
      if (parentContainer && parentContainer.scrollHeight !== lastScrollHeight) {
        resizeCanvas();
      }
      animationFrameId = requestAnimationFrame(checkResize);
    };
    
    checkResize();
    
    // Also listen to window resize
    window.addEventListener('resize', resizeCanvas);

    // Matrix characters - legal/tech themed with law icons
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789@#$%^&*()§¶←→↑↓⚖⚒';
    const lawIcons = ['⚖', '⚒']; // Law scales, hammer
    const fontSize = 12;
    
    // Gold colors
    const goldColor = 'rgba(212, 175, 55, 0.5)';
    const goldColorBright = 'rgba(218, 165, 32, 0.8)';
    const goldColorGlow = 'rgba(255, 215, 0, 0.6)';

    // Calculate columns based on canvas height (since we're going horizontal)
    const columns = Math.floor(canvas.height / fontSize);
    const drops = Array(columns).fill(0);

    // Animation function - moves horizontally (left to right)
    const draw = () => {
      // Semi-transparent black to create fading trail
      ctx.fillStyle = 'rgba(15, 15, 15, 0.08)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      ctx.font = `${fontSize}px monospace`;

      for (let i = 0; i < drops.length; i++) {
        // Occasionally use law icons (7.5% chance), otherwise regular characters
        let char;
        const useLawIcon = Math.random() < 0.075;
        
        if (useLawIcon) {
          char = lawIcons[Math.floor(Math.random() * lawIcons.length)];
        } else {
          char = chars[Math.floor(Math.random() * chars.length)];
        }
        
        const y = i * fontSize;
        const x = drops[i] * fontSize;
        
        // Special styling for law icons
        if (lawIcons.includes(char)) {
          // Law icons - smaller size and vibrant gold color
          ctx.font = `${fontSize * 0.7}px monospace`;
          ctx.shadowBlur = 20;
          ctx.shadowColor = 'rgba(255, 215, 0, 0.8)';
          ctx.fillStyle = 'rgba(255, 215, 0, 0.9)';
        } else if (i % 3 === 0) {
          // Leading character with strong glow
          ctx.font = `${fontSize}px monospace`;
          ctx.shadowBlur = 15;
          ctx.shadowColor = goldColorGlow;
          ctx.fillStyle = goldColorBright;
        } else {
          // Regular characters with subtle glow
          ctx.font = `${fontSize}px monospace`;
          ctx.shadowBlur = 8;
          ctx.shadowColor = 'rgba(218, 165, 32, 0.2)';
          ctx.fillStyle = goldColor;
        }
        
        // Draw character
        ctx.fillText(char, x, y);
        
        // Reset shadow and font after drawing
        ctx.shadowBlur = 0;
        ctx.font = `${fontSize}px monospace`;

        // Move the drop horizontally (left to right)
        // Reset to start when reaching the end to create continuous loop
        if (x > canvas.width) {
          drops[i] = 0;
        }
        
        // Randomly reset some drops to create varied starting points
        if (Math.random() > 0.975) {
          drops[i] = 0;
        }
        
        drops[i]++;
      }
    };

    const interval = setInterval(draw, 35); // Slightly faster for smoother animation

    return () => {
      clearInterval(interval);
      window.removeEventListener('resize', resizeCanvas);
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        minHeight: '100%',
        zIndex: 0,
        opacity: 0.3,
        pointerEvents: 'none',
        display: 'block'
      }}
    />
  );
};

export default HorizontalMatrixBackground;
